/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import { ArrowSquareOut, MagnifyingGlass, Warning } from "@phosphor-icons/react"
import type { ICellRendererParams } from "ag-grid-community"
import { type ComponentProps, useState } from "react"

import {
  Badge,
  Input,
  LinkButton,
  Select,
  type SelectWidth,
} from "~/components/design-system/components"
import { Tooltip } from "~/components/reusable/Tooltip"
import { RouteLink } from "~/features/feature-flags/client/primitives"
import {
  CATEGORY_FILTERS,
  type CategoryFilterValue,
} from "~/features/feature-flags/client/model/filters"
import {
  CATEGORY_DESCRIPTIONS,
  CATEGORY_LABELS,
  onOffLabel,
  type FlagReview,
} from "~/features/feature-flags/client/model/labels"
import {
  flagName,
  type FeatureFlagKey,
  type FlagCategory,
  type FlagDefinition,
} from "~/features/feature-flags/shared/registry"
import { flagSourceText, type CompanyStatus } from "~/features/feature-flags/client/model/status"
import { DateFormatType, formatDate } from "~/utils/datetime"

type ControlOption = { value: boolean | null; label: string }

const ON_OFF: ControlOption[] = [
  { value: true, label: onOffLabel(true) },
  { value: false, label: onOffLabel(false) },
]

type ControlProps = {
  /** Explains a rule that blocks this control (custom defaults, prerequisites). */
  disabledReason?: string | null
  ariaLabel: string
}

/** For a grid column of `FlagSelect`s: keys typed in the select drive its menu, not grid navigation. */
export const keepSelectKeys = ({ event }: { event: KeyboardEvent }) =>
  event.target instanceof HTMLInputElement

/**
 * On · Off for a setting with no "Default" of its own (the default itself); with `defaultValue`
 * (what "Default" currently means for this target), Default · On · Off, where `value` is null
 * while the target follows the default.
 */
export function FlagSelect({
  value,
  defaultValue,
  onChange,
  disabledReason,
  ariaLabel,
  size = "base",
}: ControlProps & {
  value: boolean | null
  defaultValue?: boolean
  onChange(value: boolean | null): void
  /** `small` inside a grid row, which is only as tall as the base control. */
  size?: ComponentProps<typeof Select>["size"]
}) {
  const reason = disabledReason ?? undefined
  const options: ControlOption[] =
    defaultValue === undefined
      ? ON_OFF
      : [{ value: null, label: `Default (${onOffLabel(defaultValue).toLowerCase()})` }, ...ON_OFF]
  return (
    <Select
      size={size}
      requires="staff"
      allowedTitle={reason}
      isDisabled={!!reason}
      aria-label={ariaLabel}
      options={options}
      value={options.find((option) => option.value === value)}
      onChange={(option) => option && onChange(option.value)}
    />
  )
}

/** A Source cell: "Needs Demand plan" with the reason on hover when a prerequisite keeps the flag
 * off, else where the value comes from. */
export function FlagSourceLabel(status: Pick<CompanyStatus, "source" | "blockedBy">) {
  const { label, reason } = flagSourceText(status)
  const text = <span className="truncate">{label}</span>
  return reason ? <Tooltip title={reason}>{text}</Tooltip> : text
}

export function CategoryBadge({ category }: { category: FlagCategory }) {
  return (
    <Tooltip title={CATEGORY_DESCRIPTIONS[category]}>
      <Badge variant="neutral" size="xs" className="shrink-0">
        {CATEGORY_LABELS[category]}
      </Badge>
    </Tooltip>
  )
}

export function CategoryCell({ data }: ICellRendererParams<{ definition: FlagDefinition }>) {
  return data ? <CategoryBadge category={data.definition.category} /> : null
}

/** A flag's Linear issue as an external link. */
export function LinearLinkView({ issue, className }: { issue: string; className?: string }) {
  return (
    <LinkButton
      href={`https://linear.app/hakio/issue/${issue}`}
      target="_blank"
      rel="noopener noreferrer"
      variant="primary"
      size="base"
      trailingIcon={<ArrowSquareOut size={16} />}
      className={className}
      onClick={(event) => event.stopPropagation()}
    >
      {issue}
    </LinkButton>
  )
}

/** A flag's name, linking to its page. */
export function FlagLink({ flagKey, className }: { flagKey: FeatureFlagKey; className?: string }) {
  return (
    <RouteLink
      to="/internal/feature-flags/$flagKey"
      params={{ flagKey }}
      variant="primary"
      size="base"
      className={className}
    >
      {flagName(flagKey)}
    </RouteLink>
  )
}

/** A custom flag's review date, with a warning once it has passed. */
export function ReviewDate({ review }: { review: FlagReview }) {
  return (
    <span className="flex items-center gap-1.5 label-base text-fg-base">
      {formatDate(review.date, DateFormatType.FullDateShort)}
      {review.warning && (
        <Tooltip title={review.warning} className="max-w-[50ch]">
          <span className="inline-flex" role="img" aria-label={review.warning}>
            <Warning size={14} className="text-fg-warning-icon" />
          </span>
        </Tooltip>
      )}
    </span>
  )
}

const CATEGORY_OPTIONS = CATEGORY_FILTERS.map((category) => ({
  value: category,
  label: category === "all" ? "All" : CATEGORY_LABELS[category],
  description: category === "all" ? undefined : CATEGORY_DESCRIPTIONS[category],
}))

/** A toolbar select over a fixed list of filter values; the first is the default. */
export function FilterSelect<T extends string>({
  label,
  width,
  options,
  value,
  onChange,
}: {
  label: string
  width: SelectWidth
  options: readonly { value: T; label: string; description?: string }[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <Select
      size="base"
      width={width}
      aria-label={label}
      options={options}
      defaultValue={options[0]}
      value={options.find((option) => option.value === value)}
      onChange={(option) => option && onChange(option.value)}
    />
  )
}

/** A search field with its icon, for a toolbar. */
export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
}) {
  return (
    <Input
      small
      variant="transparent"
      containerClassName="w-64 max-w-full"
      icon={
        <MagnifyingGlass
          size={14}
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-fg-muted"
        />
      }
      className="pl-7"
      placeholder={placeholder}
      aria-label={placeholder}
      value={value}
      clearButton
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

/** A flag list's category and search filters, and the toolbar that sets them. */
export function useFlagFilters({ placeholder }: { placeholder: string }) {
  const [category, setCategory] = useState<CategoryFilterValue>("all")
  const [search, setSearch] = useState("")
  const toolbar = (
    <div className="flex flex-wrap items-center gap-3">
      <FilterSelect
        label="Category"
        width={200}
        options={CATEGORY_OPTIONS}
        value={category}
        onChange={setCategory}
      />
      <SearchInput value={search} onChange={setSearch} placeholder={placeholder} />
    </div>
  )
  return { category, search, toolbar }
}
